import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthState, LoginRequest, RegisterRequest, AuthResponse } from '../types';
import { authApi } from '../services/api';
import Cookies from 'js-cookie';

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
  });
  const [error, setError] = useState<string | null>(null);

  // Load user from cookie on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = Cookies.get('authToken');
        if (token) {
          // Validate token with API
          const user = await authApi.validateToken(token);
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            token,
          });
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (err) {
        console.error('Failed to validate token:', err);
        Cookies.remove('authToken');
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    loadUser();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      setError(null);
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response: AuthResponse = await authApi.login(credentials);
      
      // Set cookie with 10-year expiration to match JWT token
      Cookies.set('authToken', response.token, { 
        expires: 3650, // 10 years in days
        secure: window.location.protocol === 'https:',
        sameSite: 'lax'
      });
      setAuthState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        token: response.token,
      });
    } catch (err: any) {
      setError(err.message || 'Login failed');
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      setError(null);
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const response: AuthResponse = await authApi.register(userData);
      
      // Set cookie with 10-year expiration to match JWT token
      Cookies.set('authToken', response.token, { 
        expires: 3650, // 10 years in days
        secure: window.location.protocol === 'https:',
        sameSite: 'lax'
      });
      setAuthState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        token: response.token,
      });
    } catch (err: any) {
      setError(err.message || 'Registration failed');
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const logout = () => {
    Cookies.remove('authToken');
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
    });
    setError(null);
  };

  const value: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};